// v2.18.4b — Panneau multi-arrêts (max 5 stops) avec résolveur unifié.
// L'utilisateur ne saisit JAMAIS de lat/lng. Il tape :
//   - un code CIV-VVV-CCC-XXXXX, OU
//   - un alias domicile (@marie), OU
//   - un nom de lieu du directory (commerces / santé / etc.), OU
//   - une adresse libre (fallback Mapbox), OU
//   - clique "Position GPS" pour utiliser sa position courante.
// Tout est résolu via GET /api/v3/courier/resolve-stop?q=...

import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

const PLACEHOLDERS = [
  "Ex: Cathédrale Saint-Paul",
  "Ex: CIV-ABJ-COC-7K3MX",
  "Ex: @marie",
  "Ex: Pharmacie de la Paix",
  "Ex: Aéroport FHB",
  "Ex: Restaurant Saakan Cocody",
];

const SOURCE_META = {
  civ_code: { label: "code AYA", color: "#A78BFA", bg: "rgba(167,139,250,0.15)" },
  alias: { label: "alias domicile", color: "#86EFAC", bg: "rgba(34,197,94,0.15)" },
  directory: { label: "directory AYA", color: "#93C5FD", bg: "rgba(59,130,246,0.18)" },
  mapbox: { label: "Mapbox", color: "#FBBF24", bg: "rgba(251,191,36,0.15)" },
  gps: { label: "position GPS", color: "#67E8F9", bg: "rgba(34,211,238,0.18)" },
};

async function getCurrentPos() {
  return new Promise((resolve) => {
    if (!navigator.geolocation) return resolve(null);
    navigator.geolocation.getCurrentPosition(
      (p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude }),
      () => resolve(null),
      { enableHighAccuracy: true, timeout: 6000 },
    );
  });
}

function SourceBadge({ source }) {
  const meta = SOURCE_META[source] || { label: source, color: "#94A3B8", bg: "rgba(148,163,184,0.15)" };
  return (
    <span data-testid={`stop-source-${source}`} style={{
      fontSize: 9, fontWeight: 700, letterSpacing: 0.3,
      padding: "1px 6px", borderRadius: 4,
      color: meta.color, background: meta.bg,
      border: `1px solid ${meta.color}33`,
      textTransform: "uppercase",
    }}>{meta.label}</span>
  );
}

export default function MultiStopsPanel() {
  const [open, setOpen] = useState(false);
  const [stops, setStops] = useState([]); // [{lat,lng,label,address_civ,source,sub_label}]
  const [adding, setAdding] = useState(false);
  const [query, setQuery] = useState("");
  const [resolving, setResolving] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [phIndex, setPhIndex] = useState(0);
  const [computing, setComputing] = useState(false);
  const [route, setRoute] = useState(null);

  // Rotation placeholder toutes les 2.8s pendant l'ouverture du modal.
  useEffect(() => {
    if (!adding) return;
    const id = setInterval(() => setPhIndex((i) => (i + 1) % PLACEHOLDERS.length), 2800);
    return () => clearInterval(id);
  }, [adding]);

  const reset = () => { setStops([]); setRoute(null); };

  const pushStop = (stop) => {
    if (stops.length >= 5) { toast.error("Maximum 5 arrêts."); return; }
    setStops((prev) => [...prev, stop]);
    setRoute(null);
    setAdding(false);
    setQuery("");
    setSuggestions([]);
  };

  const resolveAndAdd = async (q) => {
    const text = String(q || query).trim();
    if (!text) { toast.error("Saisis un lieu, un code AYA ou un alias."); return; }
    setResolving(true); setSuggestions([]);
    try {
      const r = await axios.get(`${API}/v3/courier/resolve-stop`, {
        params: { q: text },
        withCredentials: true,
      });
      if (r.data?.ok) {
        pushStop({
          lat: r.data.lat,
          lng: r.data.lng,
          label: r.data.label,
          address_civ: r.data.address_civ || null,
          sub_label: r.data.sub_label || null,
          source: r.data.source,
        });
        toast.success(`Arrêt ajouté : ${r.data.label}`);
      }
    } catch (e) {
      const data = e.response?.data || {};
      if (data.suggestions && data.suggestions.length > 0) {
        setSuggestions(data.suggestions);
        toast.error("Pas trouvé. Voici des suggestions :");
      } else {
        toast.error(data.message || data.error || "Résolution impossible.");
      }
    } finally { setResolving(false); }
  };

  const addFromGPS = async () => {
    setResolving(true);
    const p = await getCurrentPos();
    setResolving(false);
    if (!p) { toast.error("Position GPS indisponible."); return; }
    pushStop({
      lat: p.lat,
      lng: p.lng,
      label: "Ma position actuelle",
      address_civ: null,
      sub_label: `${p.lat.toFixed(4)}, ${p.lng.toFixed(4)}`,
      source: "gps",
    });
  };

  const removeAt = (i) => {
    setStops((prev) => prev.filter((_, idx) => idx !== i));
    setRoute(null);
  };

  const move = (i, delta) => {
    const j = i + delta;
    if (j < 0 || j >= stops.length) return;
    const arr = stops.slice();
    [arr[i], arr[j]] = [arr[j], arr[i]];
    setStops(arr);
    setRoute(null);
  };

  const computeRoute = async () => {
    if (stops.length === 0) return;
    setComputing(true);
    try {
      const origin = (await getCurrentPos()) || { lat: 5.319803, lng: -4.026111 };
      const r = await axios.post(
        `${API}/v3/courier/routes/multi`,
        { origin, stops: stops.map((s) => ({ lat: s.lat, lng: s.lng, label: s.label })) },
        { withCredentials: true },
      );
      if (r.data?.ok) {
        setRoute(r.data);
        toast.success(`Itinéraire calculé : ${r.data.total_distance_km} km · ${r.data.total_duration_minutes} min${r.data.fallback_used ? " (estimation)" : ""}`);
      } else {
        toast.error("Calcul impossible.");
      }
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    } finally { setComputing(false); }
  };

  if (!open) {
    return (
      <button
        type="button"
        data-testid="multistops-open-btn"
        onClick={() => setOpen(true)}
        style={S.openBtn}
      >🛣 Multi-arrêts</button>
    );
  }

  return (
    <div data-testid="multistops-panel" style={S.panel}>
      <div style={S.header}>
        <strong style={{ color: "#F1F5F9", fontSize: 13 }}>🛣 Multi-arrêts ({stops.length}/5)</strong>
        <button type="button" onClick={() => setOpen(false)} style={S.closeBtn}>✕</button>
      </div>

      <ul style={{ listStyle: "none", margin: 0, padding: 0, marginTop: 8 }}>
        {stops.map((s, i) => (
          <li key={i} data-testid={`stop-item-${i}`} style={S.stopItem}>
            <span style={S.stopIndex}>{i + 1}</span>
            <span style={{ flex: 1, minWidth: 0, fontSize: 12 }}>
              <div style={{ color: "#F1F5F9", display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 140 }}>{s.label || "—"}</span>
                <SourceBadge source={s.source || "directory"} />
              </div>
              {s.address_civ && (
                <div data-testid={`stop-civ-${i}`} style={{ color: "#A78BFA", fontSize: 10, fontFamily: "JetBrains Mono, Menlo, monospace" }}>{s.address_civ}</div>
              )}
              {s.sub_label && (
                <div style={{ color: "#94A3B8", fontSize: 10 }}>{s.sub_label}</div>
              )}
            </span>
            <button type="button" onClick={() => move(i, -1)} disabled={i === 0} style={S.arrowBtn}>↑</button>
            <button type="button" onClick={() => move(i, 1)} disabled={i === stops.length - 1} style={S.arrowBtn}>↓</button>
            <button type="button" data-testid={`stop-remove-${i}`} onClick={() => removeAt(i)} style={S.removeBtn}>❌</button>
          </li>
        ))}
      </ul>

      {stops.length === 0 && (
        <div style={{ fontSize: 11, color: "#64748B", padding: 10, textAlign: "center", border: "1px dashed rgba(255,255,255,0.1)", borderRadius: 6, marginTop: 8 }}>
          Aucun arrêt. Ajoute jusqu'à 5 destinations.
        </div>
      )}

      {!adding && stops.length < 5 && (
        <button
          type="button"
          data-testid="multistops-add-btn"
          onClick={() => setAdding(true)}
          style={S.addBtn}
        >➕ Ajouter un arrêt</button>
      )}

      {adding && (
        <div data-testid="multistops-add-modal" style={S.addBox}>
          <input
            type="text"
            data-testid="add-stop-input"
            placeholder={PLACEHOLDERS[phIndex]}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); resolveAndAdd(); } }}
            disabled={resolving}
            style={S.input}
            autoFocus
          />
          <div style={S.hint}>
            Tape un nom de lieu, un code AYA, un alias <code style={S.code}>@marie</code> ou utilise ta position GPS.
          </div>
          <div style={{ display: "flex", gap: 6, marginTop: 8, flexWrap: "wrap" }}>
            <button
              type="button"
              data-testid="resolve-stop-btn"
              onClick={() => resolveAndAdd()}
              disabled={resolving || !query.trim()}
              style={{ ...S.smallBtn, opacity: (resolving || !query.trim()) ? 0.5 : 1, flex: 1, minWidth: 100 }}
            >{resolving ? "Résolution…" : "✓ Résoudre"}</button>
            <button
              type="button"
              data-testid="add-stop-gps-btn"
              onClick={addFromGPS}
              disabled={resolving}
              style={{ ...S.smallBtn, background: "rgba(34,197,94,0.18)", borderColor: "rgba(34,197,94,0.4)", color: "#86EFAC" }}
            >📍 GPS</button>
            <button
              type="button"
              onClick={() => { setAdding(false); setQuery(""); setSuggestions([]); }}
              disabled={resolving}
              style={S.cancelBtn}
            >Annuler</button>
          </div>

          {suggestions.length > 0 && (
            <div data-testid="stop-suggestions" style={S.suggestionsBox}>
              <div style={{ fontSize: 10, color: "#94A3B8", marginBottom: 4 }}>Suggestions :</div>
              {suggestions.map((sg, i) => (
                <button
                  key={i}
                  type="button"
                  data-testid={`suggestion-${i}`}
                  onClick={() => { setQuery(sg.label); resolveAndAdd(sg.label); }}
                  style={S.suggestionBtn}
                >
                  <strong style={{ color: "#F1F5F9" }}>{sg.label}</strong>
                  {sg.sub && <span style={{ color: "#94A3B8", marginLeft: 6 }}>· {sg.sub}</span>}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {stops.length > 0 && (
        <button
          type="button"
          data-testid="multistops-compute-btn"
          onClick={computeRoute}
          disabled={computing}
          style={S.computeBtn}
        >{computing ? "Calcul…" : "🛣 Calculer l'itinéraire"}</button>
      )}

      {route && (
        <div data-testid="multistops-route-summary" style={S.summary}>
          <div style={{ fontSize: 12, color: "#F1F5F9" }}>
            <strong>{route.total_distance_km} km</strong> · <strong>{route.total_duration_minutes} min</strong>
            {route.fallback_used && <span style={{ color: "#FBBF24", marginLeft: 6 }}>(estimation hav.)</span>}
          </div>
          <ul style={{ listStyle: "none", padding: 0, margin: "6px 0 0 0", fontSize: 11, color: "#94A3B8" }}>
            {route.legs.map((l, i) => (
              <li key={i}>
                {l.from_index === -1 ? "Origin" : `Stop ${l.from_index + 1}`} → Stop {l.to_index + 1} : {l.distance_km} km · {l.duration_minutes} min
              </li>
            ))}
          </ul>
        </div>
      )}

      {stops.length > 0 && (
        <button type="button" onClick={reset} style={{ ...S.smallBtn, marginTop: 8, width: "100%" }}>Réinitialiser</button>
      )}
    </div>
  );
}

const S = {
  openBtn: {
    position: "fixed", left: 12, bottom: 196, zIndex: 50,
    padding: "8px 13px",
    background: "rgba(59,130,246,0.18)",
    border: "1px solid rgba(59,130,246,0.5)",
    color: "#93C5FD",
    borderRadius: 999,
    fontSize: 12, fontWeight: 700,
    cursor: "pointer",
    backdropFilter: "blur(8px)",
    fontFamily: "Inter, system-ui, sans-serif",
  },
  panel: {
    position: "fixed", left: 12, bottom: 196, zIndex: 50,
    width: "min(94vw, 340px)",
    maxHeight: "78vh", overflowY: "auto",
    padding: 12,
    background: "rgba(15, 23, 42, 0.95)",
    border: "1px solid rgba(59,130,246,0.4)",
    borderRadius: 12,
    boxShadow: "0 14px 36px rgba(0,0,0,0.55)",
    backdropFilter: "blur(12px)",
    color: "#E2E8F0",
    fontFamily: "Inter, system-ui, sans-serif",
  },
  header: { display: "flex", justifyContent: "space-between", alignItems: "center" },
  closeBtn: { background: "transparent", color: "#94A3B8", border: "none", fontSize: 16, cursor: "pointer" },
  stopItem: { display: "flex", alignItems: "flex-start", gap: 6, padding: "6px 4px", borderTop: "1px solid rgba(255,255,255,0.05)" },
  stopIndex: { width: 22, height: 22, borderRadius: "50%", background: "rgba(59,130,246,0.25)", color: "#93C5FD", fontSize: 11, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 2 },
  arrowBtn: { background: "transparent", color: "#CBD5E1", border: "1px solid rgba(255,255,255,0.1)", width: 22, height: 22, borderRadius: 4, fontSize: 11, cursor: "pointer", flexShrink: 0 },
  removeBtn: { background: "transparent", color: "#FCA5A5", border: "none", fontSize: 12, cursor: "pointer", flexShrink: 0 },
  addBtn: { width: "100%", marginTop: 10, padding: "8px", background: "rgba(34,197,94,0.18)", border: "1px solid rgba(34,197,94,0.4)", color: "#86EFAC", borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: "pointer" },
  addBox: { marginTop: 10, padding: 10, background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 8 },
  input: { width: "100%", padding: "8px 10px", background: "rgba(15,23,42,0.95)", border: "1px solid rgba(255,255,255,0.15)", color: "#F1F5F9", borderRadius: 6, fontSize: 12, fontFamily: "Inter, system-ui, sans-serif", boxSizing: "border-box" },
  hint: { fontSize: 10, color: "#64748B", marginTop: 6, lineHeight: 1.4 },
  code: { background: "rgba(167,139,250,0.15)", color: "#C4B5FD", padding: "0 4px", borderRadius: 3, fontFamily: "JetBrains Mono, Menlo, monospace", fontSize: 9 },
  smallBtn: { background: "rgba(59,130,246,0.18)", border: "1px solid rgba(59,130,246,0.4)", color: "#93C5FD", padding: "6px 10px", borderRadius: 5, fontSize: 11, cursor: "pointer", fontWeight: 600 },
  cancelBtn: { background: "transparent", color: "#94A3B8", border: "1px solid rgba(255,255,255,0.1)", padding: "6px 10px", borderRadius: 5, fontSize: 11, cursor: "pointer" },
  computeBtn: { width: "100%", marginTop: 10, padding: "8px 12px", background: "linear-gradient(135deg, #3B82F6, #60A5FA)", color: "#fff", border: "none", borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: "pointer" },
  summary: { marginTop: 10, padding: 10, background: "rgba(15,23,42,0.6)", border: "1px solid rgba(59,130,246,0.3)", borderRadius: 6 },
  suggestionsBox: { marginTop: 10, padding: 8, background: "rgba(251,191,36,0.06)", border: "1px solid rgba(251,191,36,0.2)", borderRadius: 6 },
  suggestionBtn: { display: "block", width: "100%", textAlign: "left", background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)", color: "#E2E8F0", padding: "5px 8px", borderRadius: 4, fontSize: 11, cursor: "pointer", marginTop: 4 },
};
